import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { api } from '../../services/api';
import QuestionCard from '../../components/questions/QuestionCard';
import QuestionFilters from '../../components/questions/QuestionFilters';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import AIInsights from '../../components/questions/AIInsights';
import { FiPlus, FiDownload, FiUpload, FiZap, FiFilter, FiAlertTriangle, FiSearch } from 'react-icons/fi';

const QuestionBank = () => {
  const { hasAnyRole, isAuthenticated, isLoading: authLoading } = useAuth();
  const { showError, showSuccess, showWarning } = useNotification();
  
  // State management
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [aiInsights, setAIInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  
  // Filter and pagination state
  const [filters, setFilters] = useState({
    subject: '',
    examType: '',
    difficulty: '',
    topic: '',
    status: '',
    createdBy: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questionsPerPage] = useState(20);
  
  // Sort state
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Fetch questions
  const fetchQuestions = useCallback(async () => {
    // Wait for auth to be ready
    if (authLoading) {
      console.log('⏳ Auth still loading, waiting...');
      return;
    }
    
    if (!isAuthenticated) {
      console.log('❌ User not authenticated, cannot fetch questions');
      setError('Please log in to view questions');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Fetching questions with params:', {
        page: currentPage,
        limit: questionsPerPage,
        sortBy,
        sortOrder,
        ...filters,
        search: searchQuery
      });

      const params = {
        page: currentPage,
        limit: questionsPerPage,
        sortBy,
        sortOrder,
        ...filters
      };

      // Add search query if provided
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Filter out empty values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      console.log('📡 Making API request to /questions with params:', params);
      const response = await api.get('/questions', { params });
      
      console.log('✅ Questions fetched successfully:', response.data);
      
      if (response.data.success) {
        const { questions: fetchedQuestions, pagination } = response.data.data;
        setQuestions(fetchedQuestions || []);
        setTotalPages(pagination?.totalPages || 1);
        setTotalQuestions(pagination?.totalItems || 0);
        
        console.log(`📊 Loaded ${fetchedQuestions?.length || 0} questions, page ${currentPage} of ${pagination?.totalPages || 1}`);
      } else {
        throw new Error(response.data.message || 'Failed to fetch questions');
      }
    } catch (err) {
      console.error('❌ Error fetching questions:', err);
      
      // Clear questions and reset pagination when API fails
      setQuestions([]);
      setTotalPages(1);
      setTotalQuestions(0);
      
      // Handle different error scenarios
      if (err.response?.status === 401) {
        setError('Authentication required. Please log in again.');
        // The global api interceptor will handle the redirect
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view questions.');
      } else if (err.response?.status === 404) {
        setError('Questions endpoint not found.');
      } else if (err.code === 'NETWORK_ERROR' || err.message === 'Network Error') {
        setError('Network error. Please check your connection and try again.');
      } else {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to load questions';
        setError(errorMessage);
        showError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, questionsPerPage, sortBy, sortOrder, filters, searchQuery, authLoading, isAuthenticated, showError]);

  // Fetch questions when auth is ready or dependencies change
  useEffect(() => {
    console.log('🔄 useEffect triggered - Auth state:', { authLoading, isAuthenticated });
    
    if (!authLoading && isAuthenticated) {
      console.log('✅ User is authenticated, fetching questions...');
      fetchQuestions();
    } else if (!authLoading && !isAuthenticated) {
      // If auth loading is complete but user is not authenticated, clear questions
      console.log('❌ User not authenticated, clearing questions');
      setQuestions([]);
      setError('Please log in to view questions');
      setLoading(false);
    } else {
      console.log('⏳ Auth still loading or checking...');
    }
  }, [fetchQuestions, isAuthenticated, authLoading, currentPage, questionsPerPage, sortBy, sortOrder, filters, searchQuery]);

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on search
  };

  // Handle filter change
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Handle sort change
  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  // Handle question selection
  const handleQuestionSelect = (questionId, isSelected) => {
    if (isSelected) {
      setSelectedQuestions(prev => [...prev, questionId]);
    } else {
      setSelectedQuestions(prev => prev.filter(id => id !== questionId));
    }
  };

  // Handle select all questions
  const handleSelectAll = () => {
    if (selectedQuestions.length === questions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(questions.map(q => q._id));
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    if (selectedQuestions.length === 0) {
      showWarning('Please select questions first');
      return;
    }

    try {
      setLoading(true);
      
      switch (action) {
        case 'activate':
          await api.patch('/questions/bulk-activate', {
            questionIds: selectedQuestions
          });
          showSuccess(`${selectedQuestions.length} questions activated`);
          break;
          
        case 'deactivate':
          await api.patch('/questions/bulk-deactivate', {
            questionIds: selectedQuestions
          });
          showSuccess(`${selectedQuestions.length} questions deactivated`);
          break;
          
        case 'delete':
          if (window.confirm(`Are you sure you want to delete ${selectedQuestions.length} questions?`)) {
            await api.delete('/questions/bulk-delete', {
              data: { questionIds: selectedQuestions }
            });
            showSuccess(`${selectedQuestions.length} questions deleted`);
          }
          break;
          
        case 'export':
          const exportResponse = await api.post('/questions/export', {
            questionIds: selectedQuestions
          }, {
            responseType: 'blob'
          });
          
          // Create download link
          const url = window.URL.createObjectURL(new Blob([exportResponse.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `questions_${new Date().toISOString().split('T')[0]}.json`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          
          showSuccess('Questions exported successfully');
          break;
          
        default:
          break;
      }
      
      setSelectedQuestions([]);
      fetchQuestions();
    } catch (err) {
      console.error('Bulk action error:', err);
      showError(err.response?.data?.message || 'Bulk action failed');
    } finally {
      setLoading(false);
    }
  };

  // Generate AI insights for selected questions
  const generateAIInsights = async () => {
    if (selectedQuestions.length === 0) {
      showWarning('Please select questions first');
      return;
    }

    try {
      setLoadingInsights(true);
      const response = await api.post('/questions/ai-insights', {
        questionIds: selectedQuestions
      });
      
      setAIInsights(response.data.insights);
      setShowAIInsights(true);
    } catch (err) {
      console.error('AI insights error:', err);
      showError(err.response?.data?.message || 'Failed to generate Expert Intelligence insights');
    } finally {
      setLoadingInsights(false);
    }
  };

  // Handle question update (for inline editing)
  const handleQuestionUpdate = async (questionId, updates) => {
    try {
      const response = await api.put(`/questions/${questionId}`, updates);
      const updatedQuestion = response.data.data?.question || response.data.question || response.data;
      
      setQuestions(prev => 
        prev.map(q => q._id === questionId ? updatedQuestion : q)
      );
      
      showSuccess('Question updated successfully');
    } catch (err) {
      console.error('Question update error:', err);
      showError(err.response?.data?.message || 'Failed to update question');
    }
  };

  // Handle vet/unvet (status change)
  const handleStatusChange = async (questionId, status) => {
    try {
      const response = await api.patch(`/questions/${questionId}/status`, { status });
      const updatedQuestion = response.data.data?.question || response.data.question || response.data;
      setQuestions(prev => prev.map(q => q._id === questionId ? updatedQuestion : q));
      showSuccess(status === 'approved' ? 'Question vetted' : 'Question moved to review');
    } catch (err) {
      console.error('Question status update error:', err);
      showError(err.response?.data?.message || 'Failed to update status');
    }
  };

  // Handle question deletion
  const handleQuestionDelete = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      await api.delete(`/questions/${questionId}`);
      setQuestions(prev => prev.filter(q => q._id !== questionId));
      showSuccess('Question deleted successfully');
    } catch (err) {
      console.error('Question delete error:', err);
      showError(err.response?.data?.message || 'Failed to delete question');
    }
  };

  const canCreateQuestions = hasAnyRole(['teacher', 'admin']);
  const canManageQuestions = hasAnyRole(['teacher', 'admin']);

  if (loading && questions.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Question Bank</h1>
            <p className="text-sm text-slate-500 mt-0.5">Browse and manage questions.</p>
          </div>
          {canCreateQuestions && (
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={() => window.location.href = '/questions/import'}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <FiUpload className="h-4 w-4" />
                Import
              </button>
              <button
                type="button"
                onClick={() => window.location.href = '/questions/create'}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm"
              >
                <FiPlus className="h-4 w-4" />
                Add question
              </button>
            </div>
          )}
        </div>

        {/* Search and filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              placeholder="Search by text, topic, or tags..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${showFilters ? 'bg-slate-100 border-slate-300 text-slate-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >
              <FiFilter className="h-4 w-4" />
              More
            </button>
          </div>
          {questions.length > 0 && totalQuestions > 0 && (
            <span className="text-xs text-slate-500 sm:ml-auto">
              {(currentPage - 1) * questionsPerPage + 1}–{Math.min(currentPage * questionsPerPage, totalQuestions)} of {totalQuestions}
            </span>
          )}
        </div>

        {showFilters && (
          <div className="mb-6 rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
            <QuestionFilters filters={filters} onFilterChange={handleFilterChange} onClose={() => setShowFilters(false)} />
          </div>
        )}

        {/* Bulk actions */}
        {selectedQuestions.length > 0 && canManageQuestions && (
          <div className="mb-6 flex flex-wrap items-center gap-3 py-3 px-4 rounded-lg bg-white border border-slate-200/80 shadow-sm">
            <span className="text-sm font-medium text-slate-700">{selectedQuestions.length} selected</span>
            <div className="h-4 w-px bg-slate-200" />
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => handleBulkAction('activate')} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors">Activate</button>
              <button type="button" onClick={() => handleBulkAction('deactivate')} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors">Deactivate</button>
              <button type="button" onClick={() => handleBulkAction('export')} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"><FiDownload className="h-4 w-4" /> Export</button>
              <button type="button" onClick={generateAIInsights} disabled={loadingInsights} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"><FiZap className="h-4 w-4" /> Insights</button>
              <button type="button" onClick={() => handleBulkAction('delete')} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-red-200 bg-white text-red-600 hover:bg-red-50 transition-colors">Delete</button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="relative">
          {(authLoading || loading) && questions.length === 0 && (
            <div className="flex justify-center py-16"><LoadingSpinner /></div>
          )}

          {!authLoading && !loading && error && (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
              <FiAlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
              <h3 className="text-base font-medium text-slate-900 mb-1">Unable to load questions</h3>
              <p className="text-sm text-slate-600 mb-4">{error}</p>
              <div className="flex justify-center gap-2">
                <button type="button" onClick={fetchQuestions} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm">Try again</button>
                {!isAuthenticated && (
                  <button type="button" onClick={() => window.location.href = '/login'} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors">Sign in</button>
                )}
              </div>
            </div>
          )}

          {!authLoading && !loading && !error && questions.length === 0 && isAuthenticated && (
            <div className="rounded-lg border border-slate-200 bg-white p-10 text-center">
              <h3 className="text-base font-medium text-slate-800 mb-1">
                {searchQuery || Object.values(filters).some(Boolean) ? 'No questions match your criteria' : 'No questions yet'}
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                {searchQuery || Object.values(filters).some(Boolean) ? 'Try adjusting search or filters.' : 'Create or import questions to get started.'}
              </p>
              {canCreateQuestions && (
                <div className="flex justify-center gap-2">
                  <button type="button" onClick={() => window.location.href = '/questions/create'} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm">
                    <FiPlus className="h-4 w-4" /> Create question
                  </button>
                  <button type="button" onClick={() => window.location.href = '/questions/import'} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors">
                    <FiUpload className="h-4 w-4" /> Import
                  </button>
                </div>
              )}
              {!canCreateQuestions && (
                <p className="text-sm text-slate-500">Contact your administrator to add questions.</p>
              )}
            </div>
          )}

          {!authLoading && !loading && questions.length > 0 && (
            <>
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="text-sm text-slate-600">Sort by</label>
                  <select
                    value={sortBy}
                    onChange={(e) => handleSortChange(e.target.value)}
                    className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                  >
                    <option value="createdAt">Date created</option>
                    <option value="difficulty">Difficulty</option>
                    <option value="subject">Subject</option>
                    <option value="qualityScore">Quality score</option>
                    <option value="timesUsed">Usage count</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'))}
                    className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors"
                    title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </div>
                {canManageQuestions && (
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedQuestions.length === questions.length}
                      onChange={handleSelectAll}
                      className="rounded border-slate-300 text-slate-700 focus:ring-slate-400"
                    />
                    Select all
                  </label>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                {questions.map((question) => (
                  <QuestionCard
                    key={question._id}
                    question={question}
                    isSelected={selectedQuestions.includes(question._id)}
                    onSelect={canManageQuestions ? handleQuestionSelect : null}
                    onUpdate={canManageQuestions ? handleQuestionUpdate : null}
                    onDelete={canManageQuestions ? handleQuestionDelete : null}
                    onStatusChange={canManageQuestions ? handleStatusChange : null}
                    showActions={canManageQuestions}
                    concise
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-2 py-3 rounded-lg border border-slate-200 bg-white shadow-sm">
                  <span className="text-xs text-slate-500">Page {currentPage} of {totalPages} · {totalQuestions} questions</span>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1} className="px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none">Previous</button>
                    <button type="button" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none">Next</button>
                  </div>
                </div>
              )}
            </>
          )}

        {loading && questions.length > 0 && !authLoading && (
          <div className="absolute inset-0 rounded-lg bg-white/80 flex items-center justify-center z-10">
            <LoadingSpinner size="sm" />
          </div>
        )}
      </div>

      <Modal
        isOpen={showAIInsights}
        onClose={() => setShowAIInsights(false)}
        title="Expert Intelligence insights"
        size="large"
      >
        {aiInsights && (
          <AIInsights
            insights={aiInsights}
            questionIds={selectedQuestions}
            onClose={() => setShowAIInsights(false)}
          />
        )}
      </Modal>
      </div>
    </div>
  );
};

export default QuestionBank;
